import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { RoleGuard } from './roleGuard.service';
import { AuthGuard } from './auth.guard';
import { LocationContextGuard } from './location-context.guard';
import { LegacyLocationRedirectGuard } from './legacy-location-redirect.guard';
import { CallbackComponent } from './callback.component';

// A plain `path: ':locationNumber'` would match ANY single segment, including old-style
// absolute links like `/discover` or `/care` - swallowing them as if the feature name were
// itself a location number and falling through LocationContextGuard's "unknown location"
// fallback instead of reaching the legacy-redirect guard below. Location numbers are always
// numeric, so restrict this route to that shape and let everything else fall through.
export function locationNumberMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length > 0 && /^\d+$/.test(segments[0].path)) {
    return { consumed: [segments[0]], posParams: { locationNumber: segments[0] } };
  }
  return null;
}

export const routes: Routes = [
  {
    path: 'login/callback',
    component: CallbackComponent,
  },
  {
    path: 'logged-out',
    loadChildren: () => import('./logged-out/logged-out.module').then(m => m.LoggedOutModule)
  },
  {
    path: 'eula',
    loadChildren: () => import('./eula/eula.module').then(m => m.EulaModule),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['LOGIN'] }
  },
  {
    path: 'maintenance',
    loadChildren: () => import('./maintenance/maintenance.module').then(m => m.MaintenanceModule),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['LOGIN'] }
  },
  {
    // Outside the location prefix on purpose: it is where users land when their location fails.
    path: 'location-error',
    loadChildren: () => import('./location-error/location-error.module').then(m => m.LocationErrorModule),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['LOGIN'] }
  },
  {
    matcher: locationNumberMatcher,
    canActivate: [AuthGuard, RoleGuard, LocationContextGuard],
    data: { roles: ['LOGIN'] },
    children: [
      { path: '',
        redirectTo: 'discover',
        pathMatch: 'full'
      },
      { path: '#',
        redirectTo: 'discover',
      },
      { path: 'engage',
        redirectTo: 'engage/template-selection',
        pathMatch: 'full'
      },
      {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module').then(m => m.SettingsModule)
      },
      {
        path: 'notifications',
        loadChildren: () => import('./notifications/notifications.module').then(m => m.NotificationsModule)
      },
      {
        path: 'menu',
        loadChildren: () => import('./nav-menu/nav-menu.module').then(m => m.NavMenuModule)
      },
      {
        path: 'discover',
        loadChildren: () => import('./discover/discover.module').then(m => m.DiscoverModule)
      },
      {
        path: 'meet',
        loadChildren: () => import('./meet/meet.module').then(m => m.MeetModule)
      },
      {
        path: 'customer',
        loadChildren: () => import('./customer-profile/customer-profile.module').then(m => m.CustomerProfileModule)
      },
      {
        path: 'care',
        loadChildren: () => import('./care/care.module').then(m => m.CareModule)
      },
      {
        path: 'filter',
        loadChildren: () => import('./filter/filter.module').then(m => m.FilterModule)
      },
      {
        path: 'engage',
        loadChildren: () => import('./engage/engage.module').then(m => m.EngageModule)
      },
      {
        path: 'understand',
        loadChildren: () => import('./understand/understand.module').then(m => m.UnderstandModule)
      },
      {
        path: 'calendar',
        loadChildren: () => import('./calendar/calendar.module').then(m => m.CalendarModule)
      },
      { path: '**', redirectTo: 'discover' }
    ]
  },
  { path: '**', canActivate: [AuthGuard, RoleGuard, LegacyLocationRedirectGuard], data: { roles: ['LOGIN'] }, children: [] }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      // Keep the pre-v22 default. Child routes such as /:locationNumber/discover
      // must not inherit the parent locationNumber param or roles data.
      paramsInheritanceStrategy: 'emptyOnly'
    }),
  ],
  exports: [ RouterModule ],
  providers: [
    { provide: LocationStrategy, useClass: PathLocationStrategy }
  ]
})
export class AppRoutingModule { }
